import { useState, useEffect } from 'react'

import { get, isEmpty } from 'lodash'
import { useSnackbar } from 'notistack'
import { useIntl } from 'react-intl'
import { useQuery } from 'react-query'
import * as yup from 'yup'

import { ManageEmployeeForm } from './components'
import { FormHandler } from 'components'
import { API } from 'core/requests'
import { DemographicCategory } from 'enums/demographic'
import { t } from 'i18n'
import { EmployeeType } from 'types'

const validationSchema = yup.object({
  company_internal_id: yup.string().nullable(),
  first_name: yup.string()
  .required(t('employees.validations.firstNameIsRequired')),
  last_name: yup.string()
  .required(t('employees.validations.lastNameIsRequired')),
  title: yup.string().nullable(),
  email: yup
    .string()
    .email(t('employees.validations.invalidEmailFormat'))
    .required(t('employees.validations.emailIsRequired')),
  phone_number: yup.string().nullable(),
  notification_preference: yup
    .string()
    .oneOf(['email', 'sms', 'all'])
    .nullable(),
  // SMS delivery is only possible with a mobile number, and the API rejects the
  // whole update if one is missing. Catching it here keeps the error on the
  // field instead of surfacing as a snackbar after submit.
  cell_number: yup
    .string()
    .nullable()
    .when('notification_preference', {
      is: (preference: string) => preference === 'sms' || preference === 'all',
      then: yup
        .string()
        .nullable()
        .required(t('employees.validations.cellNumberRequiredForSms')),
    }),
  // .test(
  //   'matches-phone-number',
  //   'Must be a valid US phone number.',
  //   (phone_number: any) =>
  //     !phone_number ||
  //     (phone_number &&
  //       phone_number.match(/^(\([0-9]{3}\) |[0-9]{3}-)[0-9]{3}-[0-9]{4}$/)),
  // ),
  location: yup.string(),
  department: yup.string(),
  business_unit: yup.string().nullable(),
  region: yup.string().nullable(),
  country: yup.string().nullable()
  .required(t('employees.validations.countryIsRequired')),
  territory: yup.string().nullable(),
  job_type: yup.string().nullable(),
  hired_at: yup.date().nullable().typeError(t('invalidDateFormat')),
  date_of_birth: yup.date().nullable().typeError(t('invalidDateFormat')),
  gender: yup.string().nullable(),
  ethnicity: yup.string().nullable(),
  performance_level: yup.string().nullable(),
})

const bulkEditValidationSchema = yup.object({
  location: yup.string(),
  country: yup.string().nullable(),
  department: yup.string(),
  business_unit: yup.string().nullable(),
  region: yup.string().nullable(),
  territory: yup.string().nullable(),
  job_type: yup.string().nullable(),
})

const SUPERVISOR_PAGE_SIZE = 500

// The users endpoint is paged. This used to ask for a single page of 100, which
// silently dropped every supervisor past the first hundred for larger companies.
async function fetchSupervisorCandidates() {
  const supervisors: any[] = []
  let offset = 0

  for (;;) {
    const response = await API.get('users', null, {
      queryParams: {
        role_types: ['manager', 'executive', 'ceo'],
        limit: SUPERVISOR_PAGE_SIZE,
        offset,
      },
    })

    const page = response?.users ?? []
    supervisors.push(...page)
    offset += page.length

    if (page.length < SUPERVISOR_PAGE_SIZE) break
    if (offset >= (response?.users_count ?? 0)) break
  }

  return supervisors
}

const defaultDemographicsCategories = [
  DemographicCategory.BusinessUnit,
  DemographicCategory.Department,
  DemographicCategory.Ethnicity,
  DemographicCategory.Gender,
  DemographicCategory.JobType,
  DemographicCategory.Location,
  DemographicCategory.Region,
  DemographicCategory.Territory,
]

export default function ManageEmployee({
  dialog,
  closeDialog,
  fetchRows,
  tableRef
}: any) {
  const intl = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const [selectedSuperiors, setSelectedSuperiors] = useState<any[]>([])
  const [selectedRoles, setSelectedRoles] = useState<any[]>([])
  const [bulkUsers, setBulkUsers] = useState<any[]>([])
  const [defaultDemographics, setDefaultDemographics] = useState({})
  const [detailsRequestDone, setDetailsRequestDone] = useState(false)

  const bulkEditMode = dialog.bulkMode

  const initialValues: EmployeeType = dialog.data
    ? dialog.data
    : bulkEditMode
      ? {
        location: '',
        department: '',
        business_unit: '',
        region: '',
        country: null,
        territory: '',
        job_type: '',
        high_value_talent: false,
        demographics: [],
      }
      : {
        company_internal_id: '',
        first_name: '',
        last_name: '',
        title: '',
        email: '',
        phone_number: '',
        cell_number: '',
        notification_preference: 'email',
        location: '',
        department: '',
        business_unit: '',
        region: '',
        country: null,
        territory: '',
        job_type: '',
        hired_at: null,
        date_of_birth: null,
        gender: '',
        ethnicity: '',
        performance_level: '',
        high_value_talent: false,
        demographics: [],
      }

  const employeeId = get(dialog, 'data.id')

  const {
    data: employees,
    isLoading: employeesIsLoading,
    isError: employeesIsError,
  } = useQuery('getSupervisorsEmployees', fetchSupervisorCandidates)

  // Anyone already reporting up to this employee cannot become their supervisor
  // without closing a reporting loop, so they are left out of the picker below.
  const { data: ineligibleSuperiorIds } = useQuery(
    ['getIneligibleSuperiors', employeeId],
    () =>
      API.get('employeeIneligibleSuperiors', employeeId).then(
        response => response?.user_ids ?? [],
      ),
    { enabled: !!employeeId },
  )

  const { data: roles } = useQuery('getRoles', () => API.get('roles'))
  const { data: demographics } = useQuery('getDemographics', () =>
    API.get('demographics'),
  )

  function getPayloadDemographics(payload) {
    const newDemographics: {
      category: number
      demographic: number
    }[] = []

    defaultDemographicsCategories.forEach(demographicCategory => {
      const demographicCategoryId = demographics.find(
        demographic => demographic.category === demographicCategory,
      )?.id

      if (payload[demographicCategory]) {
        newDemographics.push({
          category: demographicCategoryId,
          demographic: payload[demographicCategory],
        })
      }
    })

    const highValueTalentDemographic = demographics.find(
      d => d.category === DemographicCategory.HighValueTalent,
    )

    if (highValueTalentDemographic?.id) {
      newDemographics.push({
        category: highValueTalentDemographic?.id,
        demographic:
          highValueTalentDemographic.demographics_attributes.find(
            attribute => attribute.flag === payload.high_value_talent,
          )?.id || false,
      })
    }

    const jobTypeDemographic = demographics.find(
      d => d.category === DemographicCategory.JobType,
    )
    const jobTypeDemographicSelectedValue =
      jobTypeDemographic?.demographics_attributes.find(
        attribute => attribute.flag === payload.job_type,
      )

    if (jobTypeDemographic?.id && jobTypeDemographicSelectedValue) {
      newDemographics.push({
        category: jobTypeDemographic?.id,
        demographic: jobTypeDemographicSelectedValue?.id,
      })
    }

    return newDemographics
  }

  useEffect(() => {
    if (!isEmpty(demographics)) {
      const newDemographicCategories = {}

      demographics
        ?.filter(demographic =>
          defaultDemographicsCategories.includes(demographic.category),
        )
        ?.forEach(demographic => {
          newDemographicCategories[demographic.category] =
            demographic.demographics_attributes
        })

      setDefaultDemographics(newDemographicCategories)
    }
  }, [demographics])

  return (
    <FormHandler
      title={intl.formatMessage({ id: 'employees.title' })}
      requestUrl='employees'
      detailsRequestId={employeeId}
      customMutationRequest={(payload: any) => {
        if (isEmpty(demographics)) {
          enqueueSnackbar(
            t('employees.snackbars.error.demographicsNotLoaded'),
            { variant: 'error' },
          )
          return Promise.resolve(false)
        }

        const newDemographics = getPayloadDemographics(payload)

        const commonAttributes = {
          ...payload,
          superior_direct_reports_attributes: selectedSuperiors,
          role_users_attributes: selectedRoles,
          demographics: newDemographics,
        }

        const newPayload = bulkEditMode
          ? {
            users: bulkUsers.map(user => user.id),
            attributes: commonAttributes,
          }
          : commonAttributes

        if (bulkEditMode) return API.update('employeesBulk', newPayload)
        if (!selectedRoles.length) {
          enqueueSnackbar(t('employees.snackbars.error.assignAtLeastOneRole'), {
            variant: 'error',
          })
        } else {
          return API.createOrUpdate('employees', newPayload).then((response) => {
            if (tableRef?.current && tableRef?.current?.onQueryChange) {
              tableRef?.current?.onQueryChange();
            }

            // Failures are already surfaced by the shared response handler, which
            // raises one snackbar per message. Returning false here is what keeps
            // the dialog open so the rejected change can be corrected.
            return !response?.errors;
          }).catch(() => {
            enqueueSnackbar(t('employees.snackbars.error.updateFailed'), {
              variant: 'error',
            })
            return false
          })
        }
      }}
      detailsRequestCallback={() => {
        setDetailsRequestDone(true)
      }}
      data={dialog.data}
      closeDialog={closeDialog}
      fetchRows={fetchRows}
      initialValues={initialValues}
      validationSchema={
        bulkEditMode ? bulkEditValidationSchema : validationSchema
      }
      hideHeader
      hideActionButton
    >
      {({ formik, mutation, isEditMode }) => (
        <ManageEmployeeForm
          title={intl.formatMessage({ id: 'employees.title' })}
          formik={formik}
          mutation={mutation}
          isEditMode={isEditMode}
          bulkEditMode={bulkEditMode}
          bulkUsers={bulkUsers}
          setBulkUsers={setBulkUsers}
          selectedSuperiors={selectedSuperiors}
          setSelectedSuperiors={setSelectedSuperiors}
          employees={employees}
          ineligibleSuperiorIds={ineligibleSuperiorIds}
          employeesIsLoading={employeesIsLoading}
          employeesIsError={employeesIsError}
          demographics={demographics}
          defaultDemographics={defaultDemographics}
          roles={roles}
          selectedRoles={selectedRoles}
          setSelectedRoles={setSelectedRoles}
          detailsRequestDone={detailsRequestDone}
        />
      )}
    </FormHandler>
  )
}
